// Coin eggs: a big coin sink for veterans. An egg is bought for coins on the
// Shop page, sits in an incubator for a fixed time (wall clock, so it hatches
// while you are away) and hatches a first-stage creature of the region you
// picked with a guaranteed minimum Bloodline. Above the minimum the tier rolls
// at the natural odds (CONFIG.bloodline, renormalised to the tiers allowed).
// The Aura rolls at its own hatch odds (CONFIG.auraOneIn), never with a
// charm: the Aura Charm only ever touches hunt catches, so paid
// odds stay limited to catches. Talent, quality and trait roll as on any catch. The egg itself is account
// bound; what hatches is an ordinary creature that can be traded. Admin coins
// (or an admin-granted egg) hatch an admin-flagged creature.
//
// Prices (sim/economy.ts, mythic-idle-reports/economy-2026-10-13.md):
// - Each egg costs the same per expected Bloodline IV-or-better creature, so
//   no tier is a cheaper road to the rare tier: a II+ egg gives IV+ 1.98% of
//   the time and a III+ egg 9.17%, so 1B × 1.98% ≈ 20M and 1B × 9.17% ≈ 100M.
// - Every price is at least 4× the break-even of the economy report (the
//   price at which even half of all net coins spent on eggs adds 10% to
//   natural catches of that Bloodline or better at day 90: 2.1M, 9.5M, 103M).
// - The top egg matches the reference game's 1B: about 11 days of a grinder's
//   net coins at day 90, months for an average player.
import { CONFIG } from './config.ts';
import { LINES, type RegionId } from './species.ts';
import { REGIONS } from './grounds.ts';
import { rollCreature, type Rolls } from './rolls.ts';
import type { Rng } from './rng.ts';

export interface EggDef {
  /** Stable id: saves, ledgers and the sim key on it, so it never changes. */
  id: string;
  /** What players see. Plain egg names, never "coin" (the ids keep the old egg-ii..iv). */
  name: string;
  /** Guaranteed minimum Bloodline (index into CONFIG.bloodline: 1 = II). */
  minBloodline: number;
  price: number;
  hatchMs: number;
}

const H = 3_600_000;
export const EGGS: EggDef[] = [
  { id: 'egg-ii', name: 'Skyborn Egg', minBloodline: 1, price: 20_000_000, hatchMs: 16 * H },
  { id: 'egg-iii', name: 'Duskborn Egg', minBloodline: 2, price: 100_000_000, hatchMs: 32 * H },
  { id: 'egg-iv', name: 'Sunborn Egg', minBloodline: 3, price: 1_000_000_000, hatchMs: 48 * H },
];
export const EGG: Record<string, EggDef> = Object.fromEntries(EGGS.map((e) => [e.id, e]));

/** The egg shop opens with Hellas, at the same level as the Arena and Halls. */
export const EGG_UNLOCK_LEVEL = 150;

/**
 * Incubators: one free; one for coins; one at a trainer level; three more for
 * diamonds, bought in order at these prices. An incubator only adds capacity
 * (eggs still cost coins and roll the same odds), so selling it for diamonds
 * buys time, never better creatures. Account-bound and permanent. Six at most.
 */
export const INCUBATOR = { coinPrice: 250_000_000, milestoneLevel: 1500, diamondPrices: [50, 100, 150] as readonly number[], max: 6 };

/** An egg in an incubator. Hatches at hatchAt whether or not the trainer is online. */
export interface EggDTO {
  id: string;
  egg: string;
  region: RegionId;
  start: number;
  hatchAt: number;
  /** Paid with admin-granted coins or granted by an admin: hatches an admin-flagged creature. */
  dev?: true;
}

type EggTrainer = { level: number; eggs?: EggDTO[]; incubatorBought?: boolean; incubatorsDiamond?: number };

/** Diamond incubators bought so far (0..3); saves from before them have none. */
export function diamondIncubators(t: EggTrainer): number {
  return Math.max(0, Math.min(INCUBATOR.diamondPrices.length, Math.floor(t.incubatorsDiamond ?? 0)));
}

/** Price in diamonds of the next diamond incubator, or null once all are bought. */
export function nextIncubatorDiamonds(t: EggTrainer): number | null {
  return INCUBATOR.diamondPrices[diamondIncubators(t)] ?? null;
}

/** Incubators this trainer has: the free one, the coin one, the milestone one and the diamond ones. */
export function incubatorSlots(t: EggTrainer): number {
  return Math.min(INCUBATOR.max, 1 + (t.incubatorBought ? 1 : 0) + (t.level >= INCUBATOR.milestoneLevel ? 1 : 0) + diamondIncubators(t));
}

export function freeIncubators(t: EggTrainer): number {
  return Math.max(0, incubatorSlots(t) - (t.eggs?.length ?? 0));
}

/** Regions an egg can be set for: any region the trainer has opened. */
export function eggRegions(level: number) {
  return REGIONS.filter((r) => level >= r.base);
}

/** Chance of each Bloodline tier from an egg with this minimum: the natural odds above it, renormalised. */
export function eggBloodlineOdds(minBloodline: number): number[] {
  const bl = CONFIG.bloodline;
  const total = bl.slice(minBloodline).reduce((a, b) => a + b.pct, 0);
  return bl.map((b, i) => (i < minBloodline ? 0 : b.pct / total));
}

/** Chance an egg gives this Bloodline tier or better. */
export function eggAtLeast(minBloodline: number, tier: number): number {
  return eggBloodlineOdds(minBloodline).slice(tier).reduce((a, b) => a + b, 0);
}

export function rollEggBloodline(rng: Rng, minBloodline: number): number {
  const odds = eggBloodlineOdds(minBloodline);
  const r = rng.next();
  // Rarest first, as rollBloodline does, so float error can only favour the minimum.
  let acc = 0;
  for (let i = odds.length - 1; i > minBloodline; i--) {
    acc += odds[i];
    if (r < acc) return i;
  }
  return minBloodline;
}

/** First stages of a region's three-stage lines (eggs hatch babies), weighted by rarity as visitors are (CONFIG.spawnWeight). */
export function eggPool(region: RegionId): [string, number][] {
  return LINES.filter((l) => l.region === region && l.stages.length === 3).map((l) => [l.stages[0].id, CONFIG.spawnWeight[l.rarity]] as [string, number]);
}

/** Level an egg creature hatches at. */
export const HATCH_LEVEL = 1;

/**
 * What an egg hatches into: species from the region's pool, ordinary talent, quality and trait, the egg's
 * Bloodline, and the Aura at the natural chance (never the charmed one; the trainer's boosts are not read).
 */
export function rollEgg(rng: Rng, def: EggDef, region: RegionId): { species: string; rolls: Rolls } {
  const species = rng.weighted(eggPool(region));
  const rolls = rollCreature(rng, { auraOneIn: CONFIG.auraOneIn, species });
  rolls.bloodline = rollEggBloodline(rng, def.minBloodline);
  return { species, rolls };
}
